import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { ChevronLeft, ChevronRight, Download, Building2, Users, TrendingUp, AlertTriangle, Filter, Maximize2, Percent, ArrowUp, ArrowDown, Infinity, Save } from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { exportCashFlowToExcel } from './ExportExcelButton';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  ReferenceLine, ReferenceDot, BarChart, Bar, Cell, LabelList, ComposedChart, Line
} from 'recharts';

// Waterfall Chart Component - Shows cash flow breakdown clearly
const WaterfallChart = ({ data, formatValue, height = 380, currency = 'MXN' }) => {
  if (!data || data.length === 0) return null;

  // Calculate waterfall positions with proper stacking
  const chartData = [];
  let runningTotal = 0;
  
  data.forEach((item, index) => {
    const isStart = item.isStart;
    const isTotal = item.isTotal;
    const value = item.value;
    
    if (isStart) {
      // Starting bar - from 0 to value
      if (value >= 0) {
        chartData.push({
          ...item,
          positive: value,
          negative: 0,
          invisible: 0,
          displayValue: value,
          isPositive: true
        });
      } else {
        chartData.push({
          ...item,
          positive: 0,
          negative: Math.abs(value),
          invisible: value,
          displayValue: value,
          isPositive: false
        });
      }
      runningTotal = value;
    } else if (isTotal) {
      // Total bar - from 0 to the value
      if (value >= 0) {
        chartData.push({
          ...item,
          positive: value,
          negative: 0,
          invisible: 0,
          displayValue: value,
          isPositive: true
        });
      } else {
        chartData.push({
          ...item,
          positive: 0,
          negative: Math.abs(value),
          invisible: value,
          displayValue: value,
          isPositive: false
        });
      }
      runningTotal = value;
    } else {
      // Change bar - floating from previous total
      const prevTotal = runningTotal;
      const newTotal = runningTotal + value;
      const isPositive = value >= 0;
      
      if (isPositive) {
        // Positive change: bar goes from prevTotal upward
        chartData.push({
          ...item,
          positive: value,
          negative: 0,
          invisible: Math.max(0, prevTotal),
          displayValue: value,
          isPositive: true
        });
      } else {
        // Negative change: bar goes from newTotal up to prevTotal
        chartData.push({
          ...item,
          positive: 0,
          negative: Math.abs(value),
          invisible: Math.max(0, newTotal),
          displayValue: value,
          isPositive: false
        });
      }
      runningTotal = newTotal;
    }
  });

  // Find min/max for Y axis
  const allRunning = [];
  let total = 0;
  data.forEach(item => {
    if (item.isStart || item.isTotal) {
      total = item.value;
    } else {
      total += item.value;
    }
    allRunning.push(total);
  });
  allRunning.push(0);
  
  const minValue = Math.min(...allRunning);
  const maxValue = Math.max(...allRunning);
  const range = maxValue - minValue || 1;
  const padding = range * 0.15;

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 40, right: 30, left: 20, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" vertical={false} />
          <XAxis 
            dataKey="name" 
            stroke="#94A3B8" 
            fontSize={11}
            angle={-40}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fill: '#94A3B8' }}
          />
          <YAxis 
            stroke="#94A3B8" 
            fontSize={11}
            tickFormatter={(v) => formatValue(v)}
            domain={[minValue - padding, maxValue + padding]}
          />
          <Tooltip
            contentStyle={{ 
              backgroundColor: '#1E1E2E', 
              border: '1px solid #3B3B4F',
              borderRadius: '8px',
              fontSize: '12px'
            }}
            formatter={(value, name, props) => {
              const item = props.payload;
              if (!item) return ['', ''];
              const prefix = !item.isStart && !item.isTotal && item.isPositive ? '+' : '';
              return [`${prefix}${formatCurrency(item.displayValue, currency)}`, item.name];
            }}
            labelFormatter={() => ''}
          />
          <ReferenceLine y={0} stroke="#64748B" strokeWidth={2} />
          
          {/* Invisible bar to position the floating bars */}
          <Bar dataKey="invisible" stackId="waterfall" fill="transparent" />
          
          {/* Positive bars (green for additions, blue for totals/starts) */}
          <Bar dataKey="positive" stackId="waterfall" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => {
              const fill = (entry.isStart || entry.isTotal) ? '#3B82F6' : '#10B981';
              return <Cell key={`pos-${index}`} fill={entry.positive > 0 ? fill : 'transparent'} />;
            })}
            <LabelList 
              dataKey="displayValue" 
              position="top"
              formatter={(value) => {
                if (value === 0) return '';
                const item = chartData.find(d => d.displayValue === value);
                if (!item || !item.isPositive) return '';
                const prefix = !item.isStart && !item.isTotal ? '+' : '';
                return `${prefix}${formatValue(value)}`;
              }}
              style={{ fill: '#E2E8F0', fontSize: 11, fontWeight: 600 }}
            />
          </Bar>
          
          {/* Negative bars (red) */}
          <Bar dataKey="negative" stackId="waterfall" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => {
              return <Cell key={`neg-${index}`} fill={entry.negative > 0 ? '#EF4444' : 'transparent'} />;
            })}
            <LabelList 
              dataKey="displayValue" 
              position="top"
              formatter={(value) => {
                if (value === 0) return '';
                const item = chartData.find(d => d.displayValue === value);
                if (!item || item.isPositive) return '';
                return formatValue(value);
              }}
              style={{ fill: '#E2E8F0', fontSize: 11, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const CashFlowPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, getCalculation, updateProject } = useProject();
  const { formatTableValue, formatChartValue } = useChartPreferences();
  
  const [cashFlow, setCashFlow] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('operational');
  const [chartYear, setChartYear] = useState('all');
  const [showFullTable, setShowFullTable] = useState(false);
  const [perpEnabled, setPerpEnabled] = useState(false);
  const [perpGrowth, setPerpGrowth] = useState(2);
  const [perpCapex, setPerpCapex] = useState(0);
  const [perpSaving, setPerpSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!currentProject || currentProject.id !== projectId) {
          await fetchProject(projectId);
        }
        const [cfData, valData] = await Promise.all([
          getCalculation(projectId, 'cash-flow'),
          getCalculation(projectId, 'valuation')
        ]);
        setCashFlow(cfData);
        setValuation(valData);
        // Initialize perpetuity from project
        const proj = currentProject || {};
        if (proj?.perpetuity) {
          setPerpEnabled(proj.perpetuity.enabled || false);
          setPerpGrowth(proj.perpetuity.growth_rate ?? 2);
          setPerpCapex(proj.perpetuity.capex_replacement ?? 0);
        }
      } catch (err) {
        console.error('Error loading cash flow:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject, getCalculation]);

  const goToNext = () => navigate(`/app/project/${projectId}/valuation`);
  const goToPrev = () => navigate(`/app/project/${projectId}/income-statement`);

  const savePerpetuity = async (enabled, growth, capex) => {
    setPerpSaving(true);
    try {
      await updateProject(projectId, {
        perpetuity: { enabled, growth_rate: parseFloat(growth) || 0, capex_replacement: parseFloat(capex) || 0 }
      });
      const [cfData, valData] = await Promise.all([
        getCalculation(projectId, 'cash-flow'),
        getCalculation(projectId, 'valuation')
      ]);
      setCashFlow(cfData);
      setValuation(valData);
      toast.success(enabled ? 'Perpetuidad aplicada al flujo' : 'Perpetuidad desactivada');
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setPerpSaving(false);
    }
  };

  const currency = currentProject?.specifications?.currency || 'MXN';
  const terminalValue = valuation?.perpetuity?.terminal_value || 0;
  const numYears = currentProject?.specifications?.project_life_years || 5;
  
  // Inject terminal_value into flows and adjust subtotals for last year
  const flows = (cashFlow?.cash_flows || []).map(f => {
    if (perpEnabled && terminalValue && f.year === numYears) {
      const tv = terminalValue;
      return {
        ...f,
        terminal_value: tv,
        investing_cash_flow: f.investing_cash_flow + tv,
        project_cash_flow: f.project_cash_flow + tv,
        net_cash_flow: f.net_cash_flow + tv,
        equity_cash_flow: f.equity_cash_flow + tv,
        cumulative_cash_flow: f.cumulative_cash_flow + tv,
        cumulative_project_cf: f.cumulative_project_cf + tv,
        cumulative_equity_cf: f.cumulative_equity_cf + tv
      };
    }
    return { ...f, terminal_value: 0 };
  });
  const equityAmount = cashFlow?.equity_amount || 0;
  const debtAmount = cashFlow?.debt_amount || 0;
  const totalInvestment = cashFlow?.total_investment || 0;
  
  // Get TIR values from valuation if available
  const projectIRR = cashFlow?.project_irr || currentProject?.valuation?.project_irr || null;
  const equityIRR = cashFlow?.equity_irr || currentProject?.valuation?.equity_irr || null;

  const formatValue = (value) => {
    if (value === undefined || value === null) return '-';
    return formatTableValue(value, currency);
  };

  // Calculate payback period
  const calculatePaybackYear = (cumulativeKey) => {
    const operatingFlows = flows.filter(f => f.year > 0);
    for (let i = 0; i < operatingFlows.length; i++) {
      if (operatingFlows[i][cumulativeKey] >= 0) {
        if (i === 0) return { year: operatingFlows[i].year, exact: operatingFlows[i].year };
        const prevFlow = operatingFlows[i - 1];
        const currFlow = operatingFlows[i];
        const fraction = Math.abs(prevFlow[cumulativeKey]) / (Math.abs(prevFlow[cumulativeKey]) + currFlow[cumulativeKey]);
        return { year: prevFlow.year, exact: prevFlow.year + fraction };
      }
    }
    return null;
  };

  // Get summary data for compact table - different columns per view
  const getSummaryData = (netKey, cumulativeKey) => {
    return flows.map(f => {
      const cumulative = f[cumulativeKey] || 0;
      
      let status = 'deficit';
      if (cumulative >= 0) status = 'profit';
      else if (cumulative >= -totalInvestment * 0.5) status = 'recovery';
      
      return {
        year: f.year,
        yearLabel: f.year === 0 ? 'Año 0' : `Año ${f.year}`,
        netFlow: f[netKey] || 0,
        cumulative: cumulative,
        status
      };
    });
  };

  // Get waterfall data based on current view
  const getWaterfallData = useMemo(() => {
    if (!flows || flows.length === 0) return [];
    
    let flowData;
    if (chartYear === 'all') {
      // Include all years (including year 0) in aggregation
      flowData = flows.reduce((acc, f) => {
        Object.keys(f).forEach(key => {
          if (typeof f[key] === 'number' && key !== 'year') {
            acc[key] = (acc[key] || 0) + f[key];
          }
        });
        return acc;
      }, {});
    } else {
      flowData = flows.find(f => f.year === parseInt(chartYear)) || {};
    }
    
    if (activeView === 'operational') {
      // Waterfall: EBITDA → deducciones → Flujo Operativo → deducciones → Flujo Neto
      // CAPEX y otros ya vienen con signo correcto del backend
      const items = [
        { name: 'EBITDA', value: flowData.ebitda || 0, isStart: true },
        { name: 'Impuestos', value: -(flowData.taxes || 0) },
        { name: 'Cap. Trabajo', value: -(flowData.working_capital_change || 0) },
        { name: 'Flujo Operativo', value: flowData.operating_cash_flow || 0, isTotal: true },
        { name: 'CAPEX', value: flowData.capex || 0 }, // Already negative from backend
        { name: 'Valor Rescate', value: flowData.salvage_value || 0 },
        ...(perpEnabled && flowData.terminal_value ? [{ name: 'Perpetuidad', value: flowData.terminal_value }] : []),
        { name: 'Aport. Capital', value: flowData.equity_contribution || 0 },
        { name: 'Préstamos', value: flowData.loan_proceeds || 0 },
        { name: 'Pago Intereses', value: -(flowData.interest_payment || 0) },
        { name: 'Pago Principal', value: -(flowData.principal_payment || 0) },
        { name: 'Flujo Neto', value: flowData.net_cash_flow || 0, isTotal: true },
      ];
      // Filter out items with 0 value (except start and totals)
      return items.filter(item => item.isStart || item.isTotal || Math.abs(item.value) > 0);
      
    } else if (activeView === 'project') {
      // Waterfall: EBITDA → deducciones → FCF Proyecto
      const items = [
        { name: 'EBITDA', value: flowData.ebitda || 0, isStart: true },
        { name: 'Impuestos', value: -(flowData.taxes || 0) },
        { name: 'Cap. Trabajo', value: -(flowData.working_capital_change || 0) },
        { name: 'Flujo Operativo', value: flowData.operating_cash_flow || 0, isTotal: true },
        { name: 'CAPEX', value: flowData.capex || 0 }, // Already negative from backend
        { name: 'Valor Rescate', value: flowData.salvage_value || 0 },
        ...(perpEnabled && flowData.terminal_value ? [{ name: 'Perpetuidad', value: flowData.terminal_value }] : []),
        { name: 'FCF Proyecto', value: flowData.project_cash_flow || 0, isTotal: true },
      ];
      return items.filter(item => item.isStart || item.isTotal || Math.abs(item.value) > 0);
      
    } else {
      // Vista Accionista: FCF Proyecto → deducciones → FCF Accionista
      const items = [
        { name: 'FCF Proyecto', value: flowData.project_cash_flow || 0, isStart: true },
        { name: 'Pago Intereses', value: -(flowData.interest_payment || 0) },
        { name: 'Pago Principal', value: -(flowData.principal_payment || 0) },
        { name: 'FCF Accionista', value: flowData.equity_cash_flow || 0, isTotal: true },
      ];
      return items.filter(item => item.isStart || item.isTotal || Math.abs(item.value) > 0);
    }
  }, [flows, activeView, chartYear]);

  // Payback chart data
  const getPaybackChartData = (cumulativeKey) => {
    return flows.map(f => ({
      year: f.year === 0 ? 'Año 0' : `Año ${f.year}`,
      yearNum: f.year,
      cumulative: f[cumulativeKey] || 0,
    }));
  };

  // Full table sections - COMPLETE for all views
  const operationalSections = [
    { title: 'FLUJO OPERATIVO', color: 'text-blue-400', bgColor: 'bg-blue-500/10', rows: [
      { key: 'ebitda', label: 'EBITDA' },
      { key: 'taxes', label: 'Impuestos', negative: true },
      { key: 'working_capital_change', label: 'Cambio Cap. Trabajo', negative: true },
      { key: 'operating_cash_flow', label: 'Flujo Operativo', isSubtotal: true }
    ]},
    { title: 'FLUJO INVERSIÓN', color: 'text-amber-400', bgColor: 'bg-amber-500/10', rows: [
      { key: 'capex', label: 'CAPEX', negative: true },
      { key: 'salvage_value', label: 'Valor Rescate' },
      ...(perpEnabled && valuation?.perpetuity?.terminal_value ? [{ key: 'terminal_value', label: 'Valor Terminal (Perpetuidad)' }] : []),
      { key: 'investing_cash_flow', label: 'Flujo Inversión', isSubtotal: true }
    ]},
    { title: 'FLUJO FINANCIAMIENTO', color: 'text-cyan-400', bgColor: 'bg-cyan-500/10', rows: [
      { key: 'equity_contribution', label: 'Aportación Capital' },
      { key: 'loan_proceeds', label: 'Préstamos' },
      { key: 'interest_payment', label: 'Pago Intereses', negative: true },
      { key: 'principal_payment', label: 'Pago Principal', negative: true },
      { key: 'financing_cash_flow', label: 'Flujo Financ.', isSubtotal: true }
    ]}
  ];

  const projectSections = [
    { title: 'FLUJO OPERATIVO', color: 'text-blue-400', bgColor: 'bg-blue-500/10', rows: [
      { key: 'ebitda', label: 'EBITDA' },
      { key: 'taxes', label: 'Impuestos', negative: true },
      { key: 'working_capital_change', label: 'Cambio Cap. Trabajo', negative: true },
      { key: 'operating_cash_flow', label: 'Flujo Operativo', isSubtotal: true }
    ]},
    { title: 'FLUJO INVERSIÓN', color: 'text-amber-400', bgColor: 'bg-amber-500/10', rows: [
      { key: 'capex', label: 'CAPEX', negative: true },
      { key: 'salvage_value', label: 'Valor Rescate' },
      ...(perpEnabled && valuation?.perpetuity?.terminal_value ? [{ key: 'terminal_value', label: 'Valor Terminal (Perpetuidad)' }] : []),
      { key: 'investing_cash_flow', label: 'Flujo Inversión', isSubtotal: true }
    ]},
    { title: 'FLUJO LIBRE DEL PROYECTO', color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', rows: [
      { key: 'project_cash_flow', label: 'FCF Proyecto', isSubtotal: true }
    ]}
  ];

  const equitySections = [
    { title: 'FLUJO LIBRE DEL PROYECTO', color: 'text-blue-400', bgColor: 'bg-blue-400/10', rows: [
      { key: 'project_cash_flow', label: 'FCF Proyecto' }
    ]},
    { title: 'SERVICIO DE DEUDA', color: 'text-pink-400', bgColor: 'bg-pink-400/10', rows: [
      { key: 'interest_payment', label: 'Pago Intereses', negative: true },
      { key: 'principal_payment', label: 'Pago Principal', negative: true }
    ]},
    { title: 'APORTACIÓN DE CAPITAL', color: 'text-green-400', bgColor: 'bg-green-400/10', rows: [
      { key: 'equity_contribution', label: 'Capital Invertido', negative: true, yearZeroOnly: true }
    ]},
    { title: 'FLUJO PARA EL ACCIONISTA', color: 'text-success', bgColor: 'bg-success/10', rows: [
      { key: 'equity_cash_flow', label: 'FCF Accionista', isSubtotal: true }
    ]}
  ];

  const getCurrentSections = () => {
    switch(activeView) {
      case 'operational': return operationalSections;
      case 'project': return projectSections;
      case 'equity': return equitySections;
      default: return operationalSections;
    }
  };

  const getNetKey = () => {
    switch(activeView) {
      case 'operational': return 'net_cash_flow';
      case 'project': return 'project_cash_flow';
      case 'equity': return 'equity_cash_flow';
      default: return 'net_cash_flow';
    }
  };

  const getCumulativeKey = () => {
    switch(activeView) {
      case 'operational': return 'cumulative_cash_flow';
      case 'project': return 'cumulative_project_cf';
      case 'equity': return 'cumulative_equity_cf';
      default: return 'cumulative_cash_flow';
    }
  };

  const renderFullTable = (sections, netKey, cumulativeKey) => (
    <Table className="text-sm">
      <TableHeader>
        <TableRow className="bg-secondary/30">
          <TableHead className="w-48 sticky left-0 bg-secondary/30 z-10">CONCEPTO</TableHead>
          {flows.map(f => (
            <TableHead key={f.year} className="text-center min-w-24">
              {f.year === 0 ? 'AÑO 0' : `AÑO ${f.year}`}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sections.map((section) => (
          <React.Fragment key={section.title}>
            {/* Section header */}
            <TableRow className={cn(section.bgColor, "border-t-2 border-t-border/50")}>
              <TableCell colSpan={flows.length + 1} className={cn("font-bold text-xs uppercase tracking-wider py-2", section.color)}>
                {section.title}
              </TableCell>
            </TableRow>
            {section.rows.map((row) => {
              const isSubtotal = row.isSubtotal;
              return (
                <TableRow key={row.key} className={cn(
                  isSubtotal && "border-t border-t-border/40 border-b-2 border-b-border/30",
                  isSubtotal && section.bgColor
                )}>
                  <TableCell className={cn(
                    "sticky left-0 z-10",
                    isSubtotal ? cn("font-bold text-xs", section.bgColor, section.color) : "bg-card pl-6 text-muted-foreground"
                  )}>
                    {isSubtotal ? `= ${row.label}` : row.negative ? `(-) ${row.label}` : row.label}
                  </TableCell>
                  {flows.map(f => {
                    let value = f[row.key];
                    if (row.yearZeroOnly) value = f.year === 0 ? value : 0;
                    // For negative rows: show absolute value (the label has (-) prefix)
                    // For subtotals/normal: show actual signed value
                    const displayValue = row.negative ? Math.abs(value || 0) : value;
                    return (
                      <TableCell key={f.year} className={cn(
                        "text-center tabular-nums",
                        !row.negative && displayValue < 0 && "text-destructive",
                        isSubtotal && cn("font-bold", section.color),
                        row.negative && displayValue > 0 && "text-orange-400/80"
                      )}>
                        {formatValue(displayValue)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </React.Fragment>
        ))}
        {/* FLUJO NETO row */}
        <TableRow className="border-t-4 border-t-primary/50 bg-primary/15">
          <TableCell className="sticky left-0 bg-primary/15 font-black text-primary z-10 text-sm">FLUJO NETO</TableCell>
          {flows.map(f => (
            <TableCell key={f.year} className={cn("text-center tabular-nums font-black text-sm", f[netKey] < 0 ? "text-destructive" : "text-success")}>
              {formatValue(f[netKey])}
            </TableCell>
          ))}
        </TableRow>
        {/* ACUMULADO row */}
        <TableRow className="bg-secondary/40 border-t border-dashed border-border/50">
          <TableCell className="sticky left-0 bg-secondary/40 font-semibold text-muted-foreground z-10">ACUMULADO</TableCell>
          {flows.map(f => (
            <TableCell key={f.year} className={cn("text-center tabular-nums font-bold", f[cumulativeKey] < 0 ? "text-destructive" : "text-success")}>
              {formatValue(f[cumulativeKey])}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando flujo de efectivo...</div>
      </div>
    );
  }

  const paybackInfo = calculatePaybackYear(getCumulativeKey());
  const summaryData = getSummaryData(getNetKey(), getCumulativeKey());
  const chartData = getPaybackChartData(getCumulativeKey());
  const maxExposure = Math.min(...flows.map(f => f[getCumulativeKey()] || 0));

  const getViewColor = () => {
    switch(activeView) {
      case 'operational': return '#3B82F6';
      case 'project': return '#0EA5E9';
      case 'equity': return '#10B981';
      default: return '#3B82F6';
    }
  };

  // Get current TIR based on view
  const getCurrentTIR = () => {
    if (activeView === 'project') return projectIRR;
    if (activeView === 'equity') return equityIRR;
    return null;
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="cash-flow-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs font-medium">PASO 9 DE 10</span>
          </div>
          <h1 className="text-3xl font-bold">Flujo de Efectivo</h1>
        </div>
        <Button variant="outline" size="sm" className="gap-2"
          onClick={() => exportCashFlowToExcel(flows, currentProject?.name || 'Proyecto', currency)}
          data-testid="export-cash-flow-excel-btn">
          <Download className="w-4 h-4" />
          Exportar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <Building2 className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Inversión Total</p>
              <p className="text-lg font-bold">{formatValue(totalInvestment)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-500/10 to-transparent border-cyan-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-cyan-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Deuda ({((debtAmount / totalInvestment) * 100).toFixed(0)}%)</p>
              <p className="text-lg font-bold">{formatValue(debtAmount)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <Users className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-[10px] text-muted-foreground">Capital ({((equityAmount / totalInvestment) * 100).toFixed(0)}%)</p>
              <p className="text-lg font-bold">{formatValue(equityAmount)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="operational" className="gap-2 text-xs">
            <Building2 className="w-4 h-4" />
            Operativo
          </TabsTrigger>
          <TabsTrigger value="project" className="gap-2 text-xs">
            <TrendingUp className="w-4 h-4" />
            Proyecto
          </TabsTrigger>
          <TabsTrigger value="equity" className="gap-2 text-xs">
            <Users className="w-4 h-4" />
            Accionista
          </TabsTrigger>
        </TabsList>

        <div className="mt-4 space-y-4">
          {/* Waterfall Chart */}
          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Diagrama de Cascada (Waterfall)</CardTitle>
                <Select value={chartYear} onValueChange={setChartYear}>
                  <SelectTrigger className="w-28 h-7 text-xs">
                    <Filter className="w-3 h-3 mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {flows.map(f => (
                      <SelectItem key={f.year} value={String(f.year)}>{f.year === 0 ? 'Año 0' : `Año ${f.year}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">Visualiza el flujo de efectivo paso a paso desde el inicio hasta el resultado final</p>
            </CardHeader>
            <CardContent className="p-2">
              <div className="h-[350px]">
                <WaterfallChart data={getWaterfallData} formatValue={formatValue} height={350} currency={currency} />
              </div>
            </CardContent>
          </Card>

          {/* Bottom Row: Small Summary Table + Large Payback Chart */}
          <div className="grid grid-cols-5 gap-4">
            {/* Summary Table - Smaller (2 cols) */}
            <Card className="col-span-2">
              <CardHeader className="pb-1 pt-2 px-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xs">Resumen</CardTitle>
                  <Button variant="ghost" size="sm" className="h-5 px-2 text-[10px]" onClick={() => setShowFullTable(true)}>
                    <Maximize2 className="w-3 h-3 mr-1" />
                    Ver Detalle
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-2 overflow-x-auto">
                <Table className="text-[10px]">
                  <TableHeader>
                    <TableRow className="bg-secondary/20">
                      <TableHead className="py-0.5 h-6 sticky left-0 bg-secondary/20 z-10 min-w-16"></TableHead>
                      {summaryData.map((row) => (
                        <TableHead key={row.year} className="text-center py-0.5 h-6 min-w-20">{row.yearLabel}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="py-1 font-semibold sticky left-0 bg-card z-10">Flujo</TableCell>
                      {summaryData.map((row) => (
                        <TableCell key={row.year} className={cn("text-center tabular-nums py-1", row.netFlow < 0 ? "text-destructive" : "text-success")}>
                          {formatValue(row.netFlow)}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className="bg-secondary/10">
                      <TableCell className="py-1 font-semibold sticky left-0 bg-secondary/10 z-10">Acumulado</TableCell>
                      {summaryData.map((row) => (
                        <TableCell key={row.year} className={cn("text-center tabular-nums py-1 font-semibold", row.cumulative < 0 ? "text-destructive" : "text-success")}>
                          {formatValue(row.cumulative)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Payback Chart - Larger (3 cols) */}
            <Card className="col-span-3">
              <CardHeader className="pb-1 pt-2 px-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm">Flujo Acumulado {activeView !== 'operational' && 'y Payback Period'}</CardTitle>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Payback only shows for Project and Equity views, not Operational */}
                    {activeView !== 'operational' && paybackInfo && (
                      <div className="text-right">
                        <p className="text-[9px] text-muted-foreground">PAYBACK</p>
                        <p className="text-lg font-bold text-success">{paybackInfo.exact.toFixed(1)} Años</p>
                      </div>
                    )}
                    {activeView !== 'operational' && getCurrentTIR() !== null && (
                      <div className="text-right">
                        <p className="text-[9px] text-muted-foreground">TIR {activeView === 'project' ? 'PROYECTO' : 'ACCIONISTA'}</p>
                        <p className="text-lg font-bold text-primary">{(getCurrentTIR() * 100).toFixed(1)}%</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-2">
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                    <XAxis dataKey="year" stroke="#94A3B8" fontSize={10} />
                    <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatChartValue(v, '$')} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: '11px' }}
                      formatter={(value, name) => name === 'cumulative' ? [formatValue(value), 'Flujo Acumulado'] : [null, null]}
                    />
                    <ReferenceLine y={0} stroke="#64748B" strokeDasharray="3 3" strokeWidth={2} label={{ value: 'Break-even', position: 'right', fill: '#64748B', fontSize: 10 }} />
                    <Bar dataKey="cumulative" isAnimationActive={false} barSize={16}>
                      {chartData.map((entry, index) => (
                        <Cell 
                          key={index} 
                          fill={entry.cumulative >= 0 ? 'rgba(14,165,233,0.35)' : 'rgba(239,68,68,0.35)'}
                          stroke={entry.cumulative >= 0 ? '#0EA5E9' : '#EF4444'}
                          strokeWidth={1}
                        />
                      ))}
                    </Bar>
                    <Line 
                      type="monotone" 
                      dataKey="cumulative" 
                      stroke="#94A3B8"
                      strokeWidth={2}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        const color = payload.cumulative >= 0 ? '#0EA5E9' : '#EF4444';
                        return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} strokeWidth={1} />;
                      }}
                      activeDot={{ r: 5 }}
                      name="Flujo Acumulado"
                    />
                    {/* Payback dot only for Project and Equity views */}
                    {activeView !== 'operational' && paybackInfo && (
                      <ReferenceDot 
                        x={`Año ${Math.ceil(paybackInfo.exact)}`} 
                        y={0} 
                        r={8}
                        fill="#10B981" 
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
                
                {/* Bottom KPIs - Different per view */}
                <div className={cn("grid gap-2 mt-2", activeView === 'operational' ? 'grid-cols-1' : 'grid-cols-2')}>
                  <div className="bg-destructive/10 rounded p-2">
                    <p className="text-[9px] text-muted-foreground">EXPOSICIÓN MÁXIMA</p>
                    <p className="text-sm font-bold text-destructive">{formatValue(maxExposure)}</p>
                  </div>
                  {activeView !== 'operational' && getCurrentTIR() !== null && (
                    <div className="bg-primary/10 rounded p-2">
                      <p className="text-[9px] text-muted-foreground">TIR {activeView === 'project' ? 'PROYECTO' : 'ACCIONISTA'}</p>
                      <p className="text-sm font-bold text-primary">{(getCurrentTIR() * 100).toFixed(1)}%</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Tabs>

      {/* Full Table Modal */}
      <Dialog open={showFullTable} onOpenChange={setShowFullTable}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeView === 'operational' && 'Flujo de Caja Operativo Completo'}
              {activeView === 'project' && 'Flujo Libre del Proyecto Completo'}
              {activeView === 'equity' && 'Flujo para el Accionista Completo'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-x-auto">
            {renderFullTable(getCurrentSections(), getNetKey(), getCumulativeKey())}
          </div>
        </DialogContent>
      </Dialog>

      {/* Perpetuity / Terminal Value Section */}
      <Card className="border-violet-500/30 bg-gradient-to-br from-violet-950/20 to-background" data-testid="perpetuity-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Infinity className="w-5 h-5 text-violet-400" />
              Valor Terminal (Perpetuidad)
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor="perp-toggle" className="text-sm text-muted-foreground">
                {perpEnabled ? 'Activada' : 'Desactivada'}
              </Label>
              <Switch
                id="perp-toggle"
                checked={perpEnabled}
                onCheckedChange={(checked) => {
                  setPerpEnabled(checked);
                  if (!checked) savePerpetuity(false, perpGrowth, perpCapex);
                }}
                data-testid="perpetuity-toggle"
              />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Agrega un valor terminal asumiendo que el negocio opera a perpetuidad despues del horizonte de proyeccion. Se refleja en el ultimo año del flujo.
          </p>
        </CardHeader>

        {perpEnabled && (
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              {/* Growth rate */}
              <div className="space-y-2">
                <Label htmlFor="perp-growth" className="text-sm">Tasa de crecimiento perpetuo (g)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="perp-growth"
                    type="number"
                    min="0"
                    max="20"
                    step="0.5"
                    value={perpGrowth}
                    onChange={(e) => setPerpGrowth(e.target.value)}
                    className="w-24"
                    data-testid="perpetuity-growth-input"
                  />
                  <span className="text-sm text-muted-foreground">% anual</span>
                </div>
                <p className="text-xs text-muted-foreground">Crecimiento esperado del flujo a largo plazo. Tipicamente entre 1-4%.</p>
                {parseFloat(perpGrowth) >= (valuation?.wacc || 0) && (
                  <p className="text-xs text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    La tasa g debe ser menor al WACC ({valuation?.wacc}%)
                  </p>
                )}
              </div>
              {/* CAPEX */}
              <div className="space-y-2">
                <Label htmlFor="perp-capex" className="text-sm">CAPEX de reposicion anual</Label>
                <Input
                  id="perp-capex"
                  type="text"
                  value={typeof perpCapex === 'number' ? perpCapex.toLocaleString('es-MX') : perpCapex}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, '');
                    if (raw === '' || !isNaN(raw)) setPerpCapex(raw === '' ? 0 : parseFloat(raw));
                  }}
                  className="w-full tabular-nums"
                  data-testid="perpetuity-capex-input"
                />
                {valuation?.perpetuity?.suggested_capex > 0 && (
                  <button
                    className="text-xs text-violet-400 hover:text-violet-300 underline"
                    onClick={() => setPerpCapex(valuation.perpetuity.suggested_capex)}
                    data-testid="perpetuity-suggested-capex"
                  >
                    Usar estimado: {formatCurrency(valuation.perpetuity.suggested_capex, currency)}
                  </button>
                )}
                <p className="text-xs text-muted-foreground">Inversion anual para mantener la capacidad operativa.</p>
              </div>
            </div>

            {/* CAPEX Breakdown */}
            {valuation?.perpetuity?.capex_breakdown?.length > 0 && (
              <div className="rounded-lg border border-violet-500/20 overflow-hidden" data-testid="capex-breakdown">
                <div className="px-3 py-2 bg-violet-500/10 text-xs font-medium text-violet-300">
                  ¿Como se calcula el CAPEX sugerido?
                </div>
                <div className="divide-y divide-border/30">
                  <div className="grid grid-cols-4 px-3 py-1.5 text-xs text-muted-foreground font-medium bg-secondary/20">
                    <span>Activo</span>
                    <span className="text-right">Monto</span>
                    <span className="text-right">Vida util</span>
                    <span className="text-right">Dep. anual</span>
                  </div>
                  {valuation.perpetuity.capex_breakdown.map((item, i) => (
                    <div key={i} className="grid grid-cols-4 px-3 py-1.5 text-xs">
                      <span className="truncate">{item.concept}</span>
                      <span className="text-right tabular-nums">{formatCurrency(item.amount, currency)}</span>
                      <span className="text-right tabular-nums">{item.dep_years} años</span>
                      <span className="text-right tabular-nums">{formatCurrency(item.annual_dep, currency)}</span>
                    </div>
                  ))}
                  <div className="grid grid-cols-4 px-3 py-1.5 text-xs font-bold bg-secondary/20">
                    <span>Total CAPEX sugerido</span>
                    <span></span>
                    <span></span>
                    <span className="text-right tabular-nums text-violet-400">{formatCurrency(valuation.perpetuity.suggested_capex, currency)}</span>
                  </div>
                </div>
                <div className="px-3 py-2 text-xs text-muted-foreground bg-secondary/10">
                  Razon: Cada activo debe reponerse al final de su vida util. El CAPEX anual es el monto dividido entre los años de vida util.
                </div>
              </div>
            )}

            {/* Salvage warning */}
            {valuation?.perpetuity?.has_salvage_warning && (
              <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
                <p className="text-sm text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Cuidado: Tienes activos con valor de rescate. Al incluir perpetuidad, podrias estar contando dos veces el valor de algunos activos. Revisa que no haya duplicacion.
                </p>
              </div>
            )}

            {/* Formula Breakdown */}
            {valuation?.perpetuity?.formula_breakdown && (
              <div className="rounded-lg border border-violet-500/20 overflow-hidden" data-testid="formula-breakdown">
                <div className="px-3 py-2 bg-violet-500/10 text-xs font-medium text-violet-300">
                  ¿Como se calcula el Valor Terminal?
                </div>
                <div className="p-4 space-y-3">
                  {(() => {
                    const fb = valuation.perpetuity.formula_breakdown;
                    return (
                      <>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between py-1 border-b border-border/20">
                            <span className="text-muted-foreground">1. Flujo del ultimo año (FCF)</span>
                            <span className="font-semibold tabular-nums">{formatCurrency(fb.last_year_fcf, currency)}</span>
                          </div>
                          <div className="flex items-center justify-between py-1 border-b border-border/20">
                            <span className="text-muted-foreground">2. (-) CAPEX de reposicion</span>
                            <span className="font-semibold tabular-nums text-red-400">- {formatCurrency(fb.capex_replacement, currency)}</span>
                          </div>
                          <div className="flex items-center justify-between py-1 border-b border-border/20 bg-secondary/20 px-2 rounded">
                            <span className="font-medium">3. (=) Flujo ajustado para perpetuidad</span>
                            <span className="font-bold tabular-nums">{formatCurrency(fb.adjusted_fcf, currency)}</span>
                          </div>
                          <div className="flex items-center justify-between py-1 border-b border-border/20">
                            <span className="text-muted-foreground">4. Flujo ajustado x (1 + g)</span>
                            <span className="font-semibold tabular-nums">{formatCurrency(fb.numerator, currency)}</span>
                            <span className="text-xs text-muted-foreground ml-1">({formatCurrency(fb.adjusted_fcf, currency)} x {(1 + fb.growth_rate / 100).toFixed(4)})</span>
                          </div>
                          <div className="flex items-center justify-between py-1 border-b border-border/20">
                            <span className="text-muted-foreground">5. WACC - g</span>
                            <span className="font-semibold tabular-nums">{fb.denominator_result.toFixed(2)}%</span>
                            <span className="text-xs text-muted-foreground ml-1">({fb.denominator_wacc}% - {fb.denominator_g}%)</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-violet-500/15 border border-violet-500/30">
                          <div>
                            <span className="text-sm font-medium">6. Valor Terminal = Paso 4 / Paso 5</span>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {formatCurrency(fb.numerator, currency)} / {(fb.denominator_result / 100).toFixed(4)} = <strong className="text-violet-400">{formatCurrency(fb.terminal_value, currency)}</strong>
                            </p>
                          </div>
                          <span className="text-xl font-bold text-violet-400 tabular-nums">{formatCurrency(fb.terminal_value, currency)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end pt-2">
              <Button
                onClick={() => savePerpetuity(true, perpGrowth, perpCapex)}
                disabled={perpSaving || parseFloat(perpGrowth) >= (valuation?.wacc || 100)}
                className="gap-2 bg-violet-600 hover:bg-violet-500"
                data-testid="perpetuity-save-btn"
              >
                <Save className="w-4 h-4" />
                {perpSaving ? 'Calculando...' : 'Aplicar Perpetuidad'}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={goToPrev} className="gap-2">
          <ChevronLeft className="w-4 h-4" />
          Estado de Resultados
        </Button>
        <Button onClick={goToNext} className="gap-2">
          Valuación
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
